import React from "react";
import { Link } from "react-router-dom";
import { PHASES } from "../lib/methodology";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Lightbulb, BookOpen, Lock } from "lucide-react";

const IMPLEMENTATION_TIPS = {
    1: [
        "Envolva várias áreas da organização nas entrevistas de diagnóstico — evite uma única perspetiva.",
        "Documente a história da empresa com factos e decisões-chave (não apenas eventos positivos).",
        "Avalie a satisfação de forma quantitativa (ex. escala 1–5) e qualitativa (citações, sentimentos).",
        "Procure evidências concretas de congruência entre valores declarados e práticas reais.",
    ],
    2: [
        "Escreva a visão global em linguagem inspiradora mas mensurável.",
        "Defina entre 3 e 5 metas principais — evite dispersão.",
        "Cada meta deve ter pelo menos um indicador SMART.",
        "Defina já o ritmo de avaliação (mensal, trimestral) antes de passar ao planeamento.",
    ],
    3: [
        "Detalhe as ações por meta e atribua responsáveis com data-limite.",
        "Identifique as habilidades em falta e planeie formação/coaching.",
        "Mapeie recursos humanos, orçamentais e tecnológicos antes de avançar.",
        "Certifique-se que cada ação alimenta diretamente uma meta — se não alimenta, corte-a.",
    ],
    4: [
        "Implementar com razão (plano) e coração (cuidado com as pessoas).",
        "Registe semanalmente o que está a correr bem e mal.",
        "Distinga constrangimentos (externos) de desvios (execução) — a resposta é diferente.",
        "Não espere pela fase de avaliação para ajustar — ajuste em pequenos ciclos.",
    ],
    5: [
        "Avalie com dados e com a voz das pessoas — as duas coisas contam.",
        "Celebre vitórias publicamente. Reforça a cultura.",
        "Trate os fracassos como lições, não como culpados.",
        "Feche o ciclo e inicie novo diagnóstico — a felicidade é um processo contínuo.",
    ],
};

export default function MethodologyGuidePage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const hasActive = isSuper || user?.subscription?.active;

    if (!hasActive) {
        return (
            <div className="max-w-2xl" data-testid="methodology-locked">
                <div className="card-flat text-center py-16">
                    <Lock size={36} className="mx-auto text-[#b89b6f]" />
                    <div className="label-caps mt-6">Acesso reservado</div>
                    <h1 className="font-display text-4xl text-[#0d1628] mt-3">
                        IHTP Happiness Development Plan<sup className="text-xl">®</sup>
                    </h1>
                    <p className="text-[#52525b] mt-4 max-w-md mx-auto leading-relaxed">
                        O guia da metodologia está disponível apenas para empresas com subscrição ativa. Adquira um plano para desbloquear o guia completo de implementação.
                    </p>
                    {user?.role === "company_manager" ? (
                        <Link to="/app/subscription" className="btn-primary mt-8 inline-flex" data-testid="unlock-cta">
                            Adquirir subscrição <ArrowRight size={14} />
                        </Link>
                    ) : (
                        <p className="label-caps mt-8">Contacte o Gestor da Empresa para ativar a subscrição.</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-10" data-testid="methodology-guide-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="label-caps">Guia de implementação</div>
                    <h1 className="font-display text-5xl text-[#0d1628] mt-4">
                        IHTP Happiness Development Plan<sup className="text-2xl">®</sup>
                    </h1>
                    <p className="text-[#52525b] mt-4 max-w-2xl leading-relaxed">
                        Este é o seu manual prático para conduzir projetos de felicidade na organização. Cada fase inclui os dados a recolher, os conceitos-chave e <strong>dicas de implementação</strong> aprendidas em anos de projetos reais.
                    </p>
                </div>
                <Link to="/app/projects" className="btn-primary" data-testid="guide-cta-projects">
                    Ir para projetos <ArrowRight size={16} />
                </Link>
            </div>

            <div className="card-flat bg-[#0d1628] text-white" data-testid="guide-overview">
                <div className="flex items-center gap-3">
                    <BookOpen size={18} className="text-[#b89b6f]" />
                    <div className="label-caps text-white/60">Visão geral</div>
                </div>
                <h2 className="font-display text-3xl md:text-4xl mt-5 leading-tight max-w-3xl">
                    Cinco fases num ciclo contínuo de <span className="italic">escutar, priorizar, envolver, agir e ser resiliente</span>.
                </h2>
                <div className="grid sm:grid-cols-5 gap-0 mt-10 border-t border-white/20">
                    {PHASES.map((p) => (
                        <div key={p.num} className="pt-6 pb-2 border-r border-white/20 last:border-r-0 px-4 first:pl-0 last:pr-0">
                            <div className="font-display text-4xl text-white">0{p.num}</div>
                            <div className="text-sm font-medium mt-3 text-white">{p.title}</div>
                            <div className="label-caps mt-1" style={{ color: "#d4c8b8" }}>{p.verb}</div>
                        </div>
                    ))}
                </div>
            </div>

            <section className="space-y-16">
                {PHASES.map((phase) => (
                    <article key={phase.num} className="grid lg:grid-cols-12 gap-10" data-testid={`guide-phase-${phase.num}`}>
                        <div className="lg:col-span-4">
                            <div className="flex items-center gap-4">
                                <span className="phase-dot active">{phase.num}</span>
                                <div className="label-caps">Fase {phase.num}</div>
                            </div>
                            <h2 className="font-display text-4xl text-[#0d1628] mt-5 leading-tight">{phase.title}</h2>
                            <div className="font-display italic text-2xl text-[#b89b6f] mt-2">{phase.verb}</div>
                            <p className="text-[#52525b] mt-5 leading-relaxed">{phase.summary}</p>
                            <div className="mt-6">
                                <div className="label-caps">Conceitos-chave</div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {phase.concepts.map((c) => (
                                        <span key={c} className="text-xs border border-[#0d1628] text-[#0d1628] px-3 py-1.5">{c}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-8 space-y-5">
                            <div className="card-flat">
                                <div className="label-caps">Dados a recolher</div>
                                <ul className="mt-5 grid sm:grid-cols-2 gap-4">
                                    {phase.fields.map((f) => (
                                        <li key={f.key} className="border-b border-[#e4e4e7] pb-3">
                                            <div className="text-[#0d1628] text-sm font-medium">{f.label}</div>
                                            {f.hint && <div className="text-xs text-[#52525b] mt-1">{f.hint}</div>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="card-flat border-l-4 border-l-[#b89b6f]" data-testid={`guide-tips-${phase.num}`}>
                                <div className="flex items-center gap-3">
                                    <Lightbulb size={16} className="text-[#b89b6f]" />
                                    <div className="label-caps">Dicas de implementação</div>
                                </div>
                                <ul className="mt-5 space-y-3 text-sm text-[#0d1628] leading-relaxed">
                                    {(IMPLEMENTATION_TIPS[phase.num] || []).map((t, i) => (
                                        <li key={`tip-${phase.num}-${i}-${(t || '').slice(0, 24)}`} className="flex gap-3">
                                            <span className="font-display text-[#b89b6f] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </article>
                ))}
            </section>

            <section className="card-flat bg-[#f9f9fa] flex items-center justify-between flex-wrap gap-4" data-testid="guide-bottom-cta">
                <div>
                    <div className="label-caps">Pronto para aplicar?</div>
                    <h3 className="font-display text-2xl text-[#0d1628] mt-2">Inicie um projeto e siga o guia fase a fase.</h3>
                </div>
                <Link to="/app/projects" className="btn-primary" data-testid="guide-bottom-start">
                    Começar um projeto <ArrowRight size={16} />
                </Link>
            </section>
        </div>
    );
}
